import { useEffect, useRef, useState } from 'react';

/*
 * Covers the one path that can still burst after the seats and credits steppers
 * commit on blur: holding down +/-, which auto-repeats every 100ms. Coalesces a
 * hold into a single fetch and removes the out-of-order-response risk.
 */
export const PLAN_REQUEST_DEBOUNCE_MS = 350;

/*
 * Trailing-edge debounce keyed on a caller-supplied identity string rather than
 * on the value's object identity.
 *
 * The key matters: callers here derive query variables through chains of memos
 * rooted in props that are rebuilt every render, so the value's identity churns
 * constantly while its contents are unchanged. Debouncing on identity would
 * reset the timer every render and never settle.
 *
 * Seeded with the first value, so the initial load is not delayed. A key that
 * changes and changes back within the window settles without emitting, since
 * the pending timer is cleared and the new key already matches.
 *
 * The timer depends only on `key` and `delayMs`. The latest committed `value`
 * is copied into a ref from an effect so equivalent-content rerenders do not
 * restart the delay, and abandoned concurrent renders cannot be observed.
 */
export function useDebouncedValueByKey(value, key, delayMs) {
  const [settled, setSettled] = useState({
    key,
    value
  });
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);
  const hasSettledValue = settled.value !== null && settled.value !== undefined;
  useEffect(() => {
    if (settled.key === key) {
      return undefined;
    }
    if (!hasSettledValue) {
      setSettled({
        key,
        value: valueRef.current
      });
      return undefined;
    }
    const timer = setTimeout(() => setSettled({
      key,
      value: valueRef.current
    }), delayMs);
    return () => clearTimeout(timer);
  }, [delayMs, hasSettledValue, key, settled.key]);
  return settled.key === key || !hasSettledValue ? value : settled.value;
}